import React, { useEffect } from 'react';

import { Alert, Box, Button, Divider, Group, List, Modal, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { AxiosError, AxiosResponse } from 'axios';
import { useBlockingInvoiceError } from 'hooks/athletePackage/useBlockingInvoiceError';
import useInvoices from 'hooks/invoice/useInvoices';
import { UseMutationResult, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { AthletePackageRetrieve, ErrorMessage } from 'types';
import notificationHandler from 'utils/notifications';

import PackageErrorMiscFeeModal from './PackageErrorMiscFeeModal';

const REMOVABLE_INVOICE_STATUSES = ['UPCOMING', 'PENDING', 'PAST DUE', 'PAYMENT FAILED', 'FAILED PAST DUE'];

interface IDeleteModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeModal: () => void;
  closeDrawer: () => void;
  deleteAthletePackageMutation: UseMutationResult<AxiosResponse<null>, AxiosError<ErrorMessage>, string>;
}

const DeleteModal = ({
  opened,
  athletePackage,
  closeModal,
  closeDrawer,
  deleteAthletePackageMutation,
}: IDeleteModalProps): JSX.Element => {
  const { successNotification } = notificationHandler();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { showErrorModal, errorModalData, handleError, resetError } = useBlockingInvoiceError({
    nonBlockingErrorTitle: 'Athlete package remove failed',
    onNonBlockingError: closeModal,
  });

  const { data: removableInvoicesData } = useInvoices({
    athletePackage: athletePackage.athletePackageId,
    status: REMOVABLE_INVOICE_STATUSES,
    limit: '1',
    enabled: opened,
  });
  const removableInvoiceCount = removableInvoicesData?.pages[0].data.count ?? 0;

  const handleClose = (): void => {
    resetError();
    closeModal();
  };

  const handleGoToInvoice = (): void => {
    if (athletePackage.athlete && errorModalData?.blockingInvoiceId) {
      navigate(
        `/users/athletes/${athletePackage.athlete}/invoices?invoiceId=${errorModalData.blockingInvoiceId}`,
      );
    }
    handleClose();
  };

  useEffect(() => {
    if (!opened) {
      resetError();
    }
  }, [opened, resetError]);

  const handleDelete = (): void => {
    deleteAthletePackageMutation.mutate(athletePackage.athletePackageId, {
      onError: handleError,
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices']);
        queryClient.invalidateQueries(['athleteEvents']);
        successNotification('Athlete package remove success', 'Athlete package successfully removed.');
        resetError();
        closeModal();
        closeDrawer();
      },
    });
  };

  return (
    <>
      <Modal
        opened={opened && !showErrorModal}
        onClose={handleClose}
        size='sm'
        centered
        title={
          <Group spacing='xs'>
            <Title order={5}>Remove Package</Title>
          </Group>
        }
      >
        <Stack spacing='md' p='xs'>
          <Text size='sm'>This will permanently delete this package.</Text>

          {removableInvoiceCount > 0 && (
            <Box
              p='xs'
              sx={theme => ({
                borderRadius: theme.radius.sm,
                backgroundColor: theme.colors.orange[0],
                border: `1px solid ${theme.colors.orange[3]}`,
              })}
            >
              <Text size='sm' c='orange.7' fw={500}>
                {removableInvoiceCount} unpaid invoice(s) will be removed.
              </Text>
            </Box>
          )}

          <Alert
            variant='light'
            color='yellow'
            icon={<IconAlertTriangle size={16} />}
            title='Auto-renew will be disabled'
            radius='md'
          >
            <Text size='xs'>This will also disable auto-renew on the original package.</Text>
          </Alert>

          <Divider />

          <Stack spacing='xs'>
            <Text size='xs' fw={600} tt='uppercase' style={{ letterSpacing: '0.05em' }}>
              What happens to invoices
            </Text>
            <List spacing='xs' size='sm' center>
              <List.Item>
                <Text size='sm'>Paid invoices will be kept.</Text>
              </List.Item>
              <List.Item>
                <Text size='sm'>Unpaid invoices are deleted.</Text>
              </List.Item>
            </List>
          </Stack>

          <Divider />

          <Text size='xs' c='dimmed' fs='italic'>
            This action cannot be undone.
          </Text>

          <Group position='right' spacing='xs'>
            <Button
              variant='default'
              onClick={handleClose}
              size='xs'
              radius='md'
              disabled={deleteAthletePackageMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              size='xs'
              radius='md'
              color='red'
              leftIcon={<IconTrash size={13} />}
              loading={deleteAthletePackageMutation.isLoading}
              onClick={handleDelete}
              data-cy='athletePackageDeleteButton'
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>

      <PackageErrorMiscFeeModal
        opened={showErrorModal}
        onClose={handleClose}
        blockingInvoice={errorModalData?.blockingInvoiceId || null}
        onGoToInvoice={handleGoToInvoice}
        isChildPackageError={errorModalData?.isChildPackageError || false}
        operation='delete'
      />
    </>
  );
};

export default DeleteModal;
